import {
  Body1,
  Button,
  Card,
  CardHeader,
  Subtitle1,
  Tab,
  TabList,
  Text,
  Title3,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useAuth } from '@worklogix/web-common';
import { useLocation, useNavigate } from 'react-router-dom';

import { MOBILE, SHRINKABLE } from './responsive';
import { activeNavValue, visibleNav } from './time/nav';
import { TimeRoutes } from './time/routes';
import { useIsAdmin } from './time/useIsAdmin';
import { useIsManager } from './time/useIsManager';
import { useIsViewer } from './time/useIsViewer';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
    // Without this the page itself grows to the width of the widest table and every
    // screen scrolls sideways as a whole. See SHRINKABLE.
    ...SHRINKABLE,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow4,
    // XXL padding costs ~64px of a 390px screen — a sixth of the width before any
    // content. It also has to WRAP: the title and the user block cannot share one line.
    [MOBILE]: {
      flexWrap: 'wrap',
      rowGap: tokens.spacingVerticalS,
      paddingLeft: tokens.spacingHorizontalM,
      paddingRight: tokens.spacingHorizontalM,
    },
  },
  user: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    ...SHRINKABLE,
  },
  userName: {
    color: tokens.colorNeutralForeground2,
    // A long name/email is the single widest thing in the header. Truncate rather than
    // hide it: the signed-in identity matters, its full length does not.
    [MOBILE]: {
      maxWidth: '55vw',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  nav: {
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    // The nav must stay REACHABLE, not wrap into a two-row block that pushes the page
    // down. Swiping a tab strip sideways is the familiar phone idiom.
    [MOBILE]: {
      paddingLeft: tokens.spacingHorizontalS,
      paddingRight: tokens.spacingHorizontalS,
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      ...SHRINKABLE,
    },
  },
  main: {
    flexGrow: 1,
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    // minWidth:0 is what lets the table scrollers inside actually scroll instead of
    // stretching the page. Padding drops to S so a phone spends its width on content.
    ...SHRINKABLE,
    /**
     * THE BACKSTOP, added 2026-09-10 after the page still scrolled sideways as a
     * whole and carried the header and table off-screen with it.
     *
     * SHRINKABLE alone was not enough, and the reason is worth stating: an
     * ancestor's `maxWidth: 100%` constrains THAT ELEMENT, it does not clip an
     * overflowing CHILD. So one wide, non-scrolling descendant — a seven-tab
     * strip, a fixed-track grid — escapes every capped ancestor and lands on the
     * document, which then scrolls as a unit. Chasing each offender is
     * whack-a-mole; owning the boundary here means the WORST case degrades to a
     * scrollbar on the content region, with the header and nav staying put.
     *
     * `auto`, never `hidden`: content that overflows must stay reachable.
     */
    overflowX: 'auto',
    [MOBILE]: {
      paddingTop: tokens.spacingVerticalM,
      paddingBottom: tokens.spacingVerticalM,
      paddingLeft: tokens.spacingHorizontalS,
      paddingRight: tokens.spacingHorizontalS,
    },
  },
  landing: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
  },
  card: {
    maxWidth: '480px',
    width: '100%',
    padding: tokens.spacingVerticalL,
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalM,
  },
});

/**
 * Authenticated tenant shell for the WorkLogix WFM site. Light Fluent chrome:
 * an app-title header with the signed-in user + sign-out, a supervisor tab strip
 * (Hours & Timesheet / Employees / Kiosks), and a routed content region.
 *
 * The tab strip + routed surfaces are Manager/Admin only. Authorization is
 * enforced server-side (role + RLS); this shell hides the whole surface from
 * users it can positively identify as non-managers, falling back to a friendly
 * landing card.
 */
export function TenantShell(): JSX.Element {
  const styles = useStyles();
  const { displayName, logout } = useAuth();
  const isManager = useIsManager();
  const isAdmin = useIsAdmin();
  const isViewer = useIsViewer();
  const location = useLocation();
  const navigate = useNavigate();

  const selectedTab = activeNavValue(location.pathname);
  const navItems = visibleNav(isAdmin, isViewer);
  // View Only gets the (read-only) surface too — just the Hours & Timesheet tab.
  const showSurface = isManager || isViewer;

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <Subtitle1>WorkLogix</Subtitle1>
        <div className={styles.user}>
          <Text className={styles.userName}>{displayName ?? 'Signed in'}</Text>
          <Button appearance="subtle" onClick={() => void logout()}>
            Sign out
          </Button>
        </div>
      </header>

      {showSurface ? (
        <>
          <nav className={styles.nav} aria-label="Workforce management">
            <TabList
              selectedValue={selectedTab}
              onTabSelect={(_, data) => {
                void navigate(String(data.value));
              }}
            >
              {navItems.map((item) => (
                <Tab key={item.value} value={item.value}>
                  {item.label}
                </Tab>
              ))}
            </TabList>
          </nav>
          <main className={styles.main}>
            <TimeRoutes />
          </main>
        </>
      ) : (
        <main className={styles.landing}>
          <Card className={styles.card}>
            <CardHeader header={<Title3>WorkLogix — your workforce</Title3>} />
            <div className={styles.cardBody}>
              <Body1>Time, allowances, PTO and more are coming soon.</Body1>
            </div>
          </Card>
        </main>
      )}
    </div>
  );
}
